import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm hover:shadow-md',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border-2 border-input bg-background hover:bg-secondary hover:border-primary/30',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-secondary hover:text-secondary-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // Custom accent variants
        terracotta: 'bg-terracotta text-white hover:bg-terracotta-600 shadow-sm hover:shadow-md',
        sage: 'bg-sage text-white hover:bg-sage-600 shadow-sm hover:shadow-md',
        'dusty-blue': 'bg-dusty-blue text-white hover:bg-dusty-blue-600 shadow-sm hover:shadow-md',
        lavender: 'bg-lavender text-white hover:bg-lavender-600 shadow-sm hover:shadow-md',
        // Soft variants
        'terracotta-soft': 'bg-terracotta-100 text-terracotta-600 hover:bg-terracotta-200',
        'sage-soft': 'bg-sage-100 text-sage-600 hover:bg-sage-200',
        'dusty-blue-soft': 'bg-dusty-blue-100 text-dusty-blue-600 hover:bg-dusty-blue-200',
        'lavender-soft': 'bg-lavender-100 text-lavender-600 hover:bg-lavender-200',
        foreground: 'bg-foreground text-background hover:bg-foreground/90 shadow-sm',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 rounded-md px-3 text-xs',
        lg: 'h-12 rounded-lg px-8 text-base',
        xl: 'h-14 rounded-xl px-10 text-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
